import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { Tag, Task } from "../../types";

export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tags").delete().eq("id", id);
      if (error) throw error;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tags });
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks });

      const prevTags = queryClient.getQueryData<Tag[]>(queryKeys.tags);
      const prevTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

      // Each cache is left alone when it has not loaded: writing `[]` into it
      // would flip its query out of its pending state and drop the app's
      // loading gate, and onError's snapshot guard could not put it back.
      queryClient.setQueryData<Tag[]>(queryKeys.tags, (old) =>
        old ? old.filter((t) => t.id !== id) : old
      );

      // Remove the deleted tag from every task's tag list
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (old) =>
        old
          ? old.map((task) => ({
              ...task,
              tags: task.tags?.filter((tag) => tag.id !== id),
            }))
          : old
      );

      return { prevTags, prevTasks };
    },

    onError: (err, _, context) => {
      if (context?.prevTags)
        queryClient.setQueryData<Tag[]>(queryKeys.tags, context.prevTags);
      if (context?.prevTasks)
        queryClient.setQueryData<Task[]>(queryKeys.tasks, context.prevTasks);
      Sentry.captureException(err, { extra: { mutation: "useDeleteTag" } });
      toast.error("Failed to delete tag. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags });
    },
  });
}
